/**
 * /projects — the Projects hub: the project list and the approvals inbox, as
 * two tabs of one destination.
 *
 * **Why approvals moved here.** An approval is always an approval *of a
 * pipeline run*, and a pipeline always lives in a project. As a top-level
 * "Solutions" entry it read as a product area of its own, which it is not —
 * nothing arrives in that inbox that did not start in a project. Sitting it
 * beside the list keeps the gate next to the thing being gated.
 *
 * **Each tab keeps its own URL** (`/projects` and `/projects/approvals`), so a
 * link to a waiting approval still lands on it, the browser's back button
 * steps between tabs, and a refresh does not silently return you to the list.
 * The old `/approvals` path redirects here rather than 404-ing — links to it
 * exist in notifications already sent.
 */

import { Stack, Tabs, Title } from "@mantine/core";
import { IconFolders, IconGavel } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApprovalsInbox } from "@/features/pipelines/ApprovalsInbox";
import { ProjectsList } from "./ProjectsList";

export type ProjectsTab = "projects" | "approvals";

const PATH_OF: Record<ProjectsTab, string> = {
  projects: "/projects",
  approvals: "/projects/approvals",
};

export function ProjectsHub({ tab }: { tab: ProjectsTab }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Stack gap="md">
      <Title order={2}>Projects</Title>

      <Tabs
        value={tab}
        onChange={(next) => {
          const to = PATH_OF[(next ?? "projects") as ProjectsTab];
          if (to !== pathname) navigate(to);
        }}
        // Without this both panels mount, so opening Projects would also fire
        // the approvals request — and every poll after it.
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="projects" leftSection={<IconFolders size={16} />}>
            Projects
          </Tabs.Tab>
          <Tabs.Tab value="approvals" leftSection={<IconGavel size={16} />}>
            Approvals
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="projects" pt="md">
          <ProjectsList />
        </Tabs.Panel>
        <Tabs.Panel value="approvals" pt="md">
          <ApprovalsInbox />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
